import DiagnosticsModule from '@modules/DiagnosticsModule';
import { TechnicianTableRow } from './types';

export interface TechnicianCache {
  timestamp: number;
  expiresAt: number;
  tenantIds: string[];
  data: TechnicianTableRow[];
}

export type CacheChangeHandler = (
  cache: TechnicianCache | null,
  userId: string,
) => void;

export const CACHE_TTL_HOURS: number = 8;
export const CACHE_TTL_MS = CACHE_TTL_HOURS * 60 * 60 * 1000;

const CACHE_KEY_PREFIX = 'ccp_technicians_';

export class TechnicianCacheStore {
  private readonly handlers = new Set<CacheChangeHandler>();

  constructor() {
    window.addEventListener('storage', this.onStorageEvent);
  }

  // Handles storage events from other tabs, keeping state in sync across windows.
  private readonly onStorageEvent = (event: StorageEvent): void => {
    if (!event.key) return;
    const userId = this.userIdFromKey(event.key);
    if (!userId) return;
    try {
      const cache = event.newValue
        ? (JSON.parse(event.newValue) as TechnicianCache)
        : null;
      this.emit(cache, userId);
    } catch {
      this.emit(null, userId);
    }
  };

  private userIdFromKey(key: string): string | null {
    return key.startsWith(CACHE_KEY_PREFIX)
      ? key.slice(CACHE_KEY_PREFIX.length)
      : null;
  }

  private emit(cache: TechnicianCache | null, userId: string): void {
    this.handlers.forEach((h) => h(cache, userId));
  }

  private getKey(userId: string): string {
    return `${CACHE_KEY_PREFIX}${userId}`;
  }

  /** Removes the stored entry and notifies all registered handlers. */
  private clear(userId: string): void {
    localStorage.removeItem(this.getKey(userId));
    this.emit(null, userId);
  }

  /** Returns true when sorted tenant sets are identical. */
  private tenantIdsMatch(cache: TechnicianCache, tenantIds: string[]): boolean {
    const sortedCached = [...cache.tenantIds]
      .sort((a, b) => a.localeCompare(b))
      .join(',');
    const sortedCurrent = [...tenantIds]
      .sort((a, b) => a.localeCompare(b))
      .join(',');
    return sortedCached === sortedCurrent;
  }

  private isExpired(cache: TechnicianCache): boolean {
    return cache.expiresAt <= Date.now();
  }

  // Guards against pre-V3 cache entries where technician IDs were stored as
  // numbers rather than strings, which breaks downstream string comparisons.
  private isStaleV2(cache: TechnicianCache): boolean {
    return cache.data.some((entry) => typeof entry.id !== 'string');
  }

  /**
   * Registers a handler that is called whenever the cache changes for any user,
   * including updates triggered by other browser tabs via storage events.
   */
  public on(handler: CacheChangeHandler): void {
    this.handlers.add(handler);
  }

  /** Removes a previously registered change handler. */
  public off(handler: CacheChangeHandler): void {
    this.handlers.delete(handler);
  }

  /** Removes the window storage listener and all registered handlers. Call in tests to prevent listener accumulation. */
  public destroy(): void {
    window.removeEventListener('storage', this.onStorageEvent);
    this.handlers.clear();
  }

  /**
   * Returns the cached entry for the given user and tenant set, or null if the
   * entry is missing, corrupt, expired, stale (V2 format), or for a different
   * tenant set. Corrupt and invalid entries are automatically cleared from
   * storage. Tenant mismatches return null without clearing, since the stored
   * entry remains valid for its original tenant set.
   */
  public read(userId: string, tenantIds: string[]): TechnicianCache | null {
    let raw: string | null;
    try {
      raw = localStorage.getItem(this.getKey(userId));
    } catch {
      return null;
    }
    if (!raw) return null;
    try {
      const cache = JSON.parse(raw) as TechnicianCache;
      if (this.isStaleV2(cache) || this.isExpired(cache)) {
        this.clear(userId);
        return null;
      }
      if (!this.tenantIdsMatch(cache, tenantIds)) {
        return null;
      }
      return cache;
    } catch {
      this.clear(userId);
      return null;
    }
  }

  /**
   * Persists technician data for the given user and tenant set, then notifies
   * all registered handlers. Tenant IDs are sorted before storage so that
   * order-independent comparisons work correctly on read. Storage quota errors
   * are reported to Diagnostics rather than thrown.
   */
  public write(
    userId: string,
    data: TechnicianTableRow[],
    tenantIds: string[],
  ): void {
    try {
      const now = Date.now();
      const cache: TechnicianCache = {
        timestamp: now,
        expiresAt: now + CACHE_TTL_MS,
        tenantIds: [...tenantIds].sort((a, b) => a.localeCompare(b)),
        data,
      };
      localStorage.setItem(this.getKey(userId), JSON.stringify(cache));
      this.emit(cache, userId);
    } catch (e) {
      DiagnosticsModule.reportBackgroundError(
        e,
        'Failed to write technician cache',
      );
    }
  }
}
